import React from 'react';
import { Box, ExpandableSection, SpaceBetween } from '@amzn/awsui-components-console';

/**
 * Help panel content for the Attribute Management page.
 *
 * Registered in the page-keyed help registry (`helpContent.attributeManagement`
 * in commonComponents/helpPanel/HelpPanelContent.tsx) and rendered by
 * AppHelpPanel in the AppLayout tools drawer, selected via the
 * `helpContext: 'attributeManagement'` mapping on the route in routeConfig.ts.
 * The title and "Quick Links" footer are supplied by the registry entry.
 */
export default function AttributeManagementHelpPanel() {
	return (
		<SpaceBetween size="s">
			<Box>
				Attribute Management lets you define and maintain the reusable attributes used
				across SNOW workflows and operations. An attribute describes a single input or
				data field &mdash; its data type, how it is rendered to users, validation rules,
				and default behavior &mdash; so it can be consistently referenced wherever it is
				needed.
			</Box>

			<ExpandableSection headerText="What You Can Do" defaultExpanded>
				<ul>
					<li><Box variant="strong">View Attributes:</Box> Browse all attributes in a searchable, sortable table showing name, ID, data type, and description</li>
					<li><Box variant="strong">Add Attribute:</Box> Create a new attribute with its data type, rendering, validation, and defaults</li>
					<li><Box variant="strong">View Details:</Box> Select an attribute name to inspect its full configuration</li>
					<li><Box variant="strong">Edit Attribute:</Box> Update an existing attribute&apos;s configuration (the attribute name itself cannot be changed)</li>
				</ul>
			</ExpandableSection>

			<ExpandableSection headerText="Adding or Editing an Attribute">
				<ol>
					<li><Box variant="strong">Open the Form:</Box> Click &quot;Add Attribute&quot; from the list, or choose &quot;Edit Attribute&quot; from an existing row</li>
					<li><Box variant="strong">Provide the Attribute Name:</Box> A unique name is required. In edit mode the name is locked &mdash; editing an existing name creates a customisation under that name rather than renaming it</li>
					<li><Box variant="strong">Configure the Fields:</Box> Set the data type, rendering, status, validation, and any defaults (see the field reference below)</li>
					<li><Box variant="strong">Save:</Box> Click &quot;Create Attribute&quot; or &quot;Update Attribute&quot;. The list refreshes to reflect your change</li>
				</ol>
			</ExpandableSection>

			<ExpandableSection headerText="Field Reference">
				<ul>
					<li><Box variant="strong">Attribute Name:</Box> Unique identifier for the attribute. Required and immutable once created</li>
					<li><Box variant="strong">Data Type:</Box> The underlying value type (for example String, Integer, Boolean)</li>
					<li><Box variant="strong">Description:</Box> A short explanation of what the attribute represents</li>
					<li><Box variant="strong">Rendering Type:</Box> How the field is displayed &mdash; e.g. Text, Select, or Multiselect. Select/Multiselect require Options</li>
					<li><Box variant="strong">Status:</Box> Whether the attribute is Active or Inactive</li>
					<li><Box variant="strong">Attribute Type:</Box> Determines how the attribute value is scoped within a workflow</li>
					<li><Box variant="strong">Case Handling Type:</Box> Controls how the attribute value&apos;s casing is normalized</li>
					<li><Box variant="strong">Regex:</Box> Optional validation pattern the value must match</li>
					<li><Box variant="strong">Help Text:</Box> Hint text shown to users filling in the attribute</li>
					<li><Box variant="strong">Options:</Box> Comma-separated choices, required for Select and Multiselect rendering types</li>
					<li><Box variant="strong">Default Value:</Box> A pre-filled value used when none is provided</li>
					<li><Box variant="strong">Mandatory:</Box> Marks the attribute as required input</li>
					<li><Box variant="strong">Owner:</Box> The team or individual responsible for the attribute</li>
				</ul>
			</ExpandableSection>

			<ExpandableSection headerText="Tips & Best Practices">
				<ul>
					<li>Use clear, descriptive names and descriptions so attributes are easy to discover and reuse</li>
					<li>Choose the narrowest data type and add a regex where validation matters to catch bad input early</li>
					<li>Provide Options and Help Text for Select/Multiselect attributes to guide users</li>
					<li>Mark an attribute Inactive instead of deleting it when it should no longer be used in new workflows</li>
				</ul>
			</ExpandableSection>
		</SpaceBetween>
	);
}
